import { useState, useEffect, useMemo } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ChartProps } from '../Interfaces/Charts';
import { Button } from 'react-bootstrap';
import { Download } from '../Utils/Downloader';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, POPOUT_MESSAGE, REAL_TIME_RSG_COMMITMENTS_SUFFIX } from '../Constants';

export default function RealTimeRSGCommitments(props: ChartProps) {
    // ***************************************************************************************************************************************************
    // *** Custom Block: the following two defintions are custom to each table this code can produce,
    //      they provide the location for the data and the names of the columns to display
    const api_url = `${process.env.REACT_APP_PUBLIC_API_URL}${REAL_TIME_RSG_COMMITMENTS_SUFFIX}`;
    const download_filename = 'RealTimeRSGCommitments';
    const popout_path = '/charts/realtimersgcommitments';
    const modalHeader: React.ReactNode = <span>Real-Time RSG Commitments</span>;

    interface IRow {
        COMMIT_REASON: string;
        TOTAL_ECON_MAX: number | string;
        NUM_RESOURCES: number | string; // May be "3 or less"
    }
    
    function extractRowDataAndIntervalString(json: any) {
        const rowData = json.Commitment ?? [];
        const intervalString = json.RefId ?? '';
        return { rowData, intervalString };
    }

    const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
        return (
            <>
                <span>
                    <b>RSG Table Definitions:</b> Market Interval End in Eastern Standard Time (Mkt_Int_End_EST)
                    <br />
                    <b>Commit Reason:</b> The Constraint Name for all resources committed to manage constraints – 'CAPACITY' for all resources that are NOT committed to manage constraints
                    <br />
                    <b>Total Economic Maximum Limit:</b> The total As-Offered Economic Maximum Limits for all resources committed for the specified Commit Reason
                    <br />
                    <b>Number of Resources:</b> The number or count of resources committed for the specified Commit Reason. When 3 or less resources are committed, '3 or less' is displayed
                </span>
                <br />
                <br /> <span>This table is updated every 5 minutes.</span>
            </>
        );
    }, []);
    // ***************************************************************************************************************************************************

    const [buttonClicked] = [props.buttonClicked];
    const [showModal, setShowModal] = useState(false);
    const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);
    const [rawData, setRawData] = useState('');

    useEffect(() => {
        let downloadBody: React.ReactNode = (
            <div className='download-modal'>
                <Button className='download-btn' onClick={() => Download(api_url, download_filename, 'json')}>
                    Download JSON
                </Button>
            </div>
        );

        switch (buttonClicked) {
            case DESCRIPTION_MESSAGE:
                setModalBody(descriptionBody);
                setShowModal(true);
                break;
            case DOWNLOAD_MESSAGE:
                setModalBody(downloadBody);
                setShowModal(true);
                break;
            case POPOUT_MESSAGE:
                const newWinddow = window.open(popout_path, '_blank', 'width=800,height=600,noopener,noreferrer');
                if (newWinddow) newWinddow.opener = null;
                break;
            default:
                break;
        }
    }, [buttonClicked, rawData, api_url, descriptionBody]);

    const useFetchJson = <T,>(url: string) => {
        const [data, setData] = useState<T[]>();
        const [loading, setLoading] = useState(false);
        const [dataInterval, setDataInterval] = useState<string>('');
        const [error, setError] = useState<string>('');

        useEffect(() => {
            let intervalId: NodeJS.Timeout;
            const fetchData = async () => {
                setLoading(true);
                setError(''); // Reset error state before new fetch
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error();
                    const json = await response.json();
                    setRawData(json);
                    const { rowData, intervalString } = extractRowDataAndIntervalString(json);
                    setDataInterval(intervalString);
                    // Coerce numeric-looking fields for proper numeric sorting
                    const numericFields = ['TOTAL_ECON_MAX', 'NUM_RESOURCES'];
                    const coerced = rowData.map((r: any) => {
                        const clone: any = { ...r };
                        numericFields.forEach(f => {
                            if (clone[f] !== undefined && clone[f] !== null) {
                                const raw = String(clone[f]).replace(/,/g, '');
                                const num = parseFloat(raw);
                                if (!Number.isNaN(num)) clone[f] = num; // leaves non-numeric strings (e.g. "N/A") untouched
                            }
                        });
                        return clone;
                    });
                    setData(coerced);
                } catch (error) {
                    setData([]); // On error, set data to empty array
                    setError('Data Connection Failed'); // Set error message
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
            intervalId = setInterval(fetchData, 5 * 60 * 1000); // Repeat every 5 minutes
            return () => clearInterval(intervalId);
        }, [url]);

        return { data, loading, dataInterval, error };
    };

    const { data, loading, dataInterval, error } = useFetchJson<IRow>(api_url);

    // Sorting state (controlled)
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const sortedData = useMemo(
        () => orderBy(data || [], sort),
        [data, sort]
    );

    if (loading) {
        return <div className='loading-and-error-box'>Loading...</div>;
    }
    if (error) {
        return <div className='loading-and-error-box'>{error}</div>;
    }
    if (!data || data.length === 0) {
        return <div className='loading-and-error-box'>No Data Available</div>;
    }

    return (
        <>
            <VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
            <div className='interval-line'>{dataInterval}</div>
            <div className='full-width-grid-wrapper'>
                <Grid
                    data={sortedData}
                    style={{ width: '100%' }}
                    scrollable="none"
                    resizable
                    sortable
                    sort={sort}
                    onSortChange={(e) => setSort(e.sort)}
                >
                    <Column field="COMMIT_REASON" title="Commit Reason" width="400px" />
                    <Column field="TOTAL_ECON_MAX" title="Total Econ Max" className="k-text-end" />
                    <Column field="NUM_RESOURCES" title="Number Resources" className="k-text-end" />
                </Grid>
            </div>
        </>
    );
}
